import { useState } from "react";
import axios from "axios";
import {
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  Box,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import { tokens } from "../../theme";

// Components
import RepresentacionNatural from "../../components/proteccionComp/Natural";
import RepresentacionJuridica from "../../components/proteccionComp/Juridico";
import Proveedor from "../../components/proteccionComp/Proveedor";
import DenunciaForm from "../../components/proteccionComp/Denuncia";

const apiURL = process.env.REACT_APP_API_URLDB;

const ProteccionUsuario = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const mainBlue = "#273c7d";

  const [representacion, setRepresentacion] = useState(null);
  const [step, setStep] = useState(1);

  const [formData, setFormData] = useState({
    idSolicitud: "",
    natural: {
      nombre: "",
      dni: "",
      depto: "",
      municipio: "",
      colonia: "",
      calle: "",
      casa: "",
      referencia: "",
      fijo: "",
      celular: "",
      correo: "",
    },
    juridico: {
      razonSocial: "",
      dni: "",
      rtn: "",
      apoderado: "",
      depto: "",
      municipio: "",
      domicilio: "",
      telefono: "",
      correo: "",
    },
    proveedor: {},
    denuncia: {},
  });

  const handleSelectChange = (event) => {
    setRepresentacion(event.target.value);
  };

  const handleDataChange = (stepData, stepName) => {
    setFormData((prevData) => ({
      ...prevData,
      [stepName]: { ...prevData[stepName], ...stepData },
    }));
  };

  const getIdSolicitud = async () => {
    try {
      const response = await axios.get(`${apiURL}/idSolicitud`);
      return response.data;
    } catch (error) {
      console.error("Error al obtener el ID de solicitud:", error);
      return null;
    }
  };

  const handleSubmit = async (data) => {
    const idSolicitud = await getIdSolicitud();
    if (!idSolicitud) {
      console.error("No se pudo obtener el ID de solicitud.");
      return;
    }

    const solicitudCompleta = {
      idSolicitud,
      representacion,
      proveedor: formData.proveedor,
      denuncia: data,
      natural: representacion === "natural" ? formData.natural : null,
      juridico: representacion === "juridico" ? formData.juridico : null,
    };

    axios
      .post(`${apiURL}/crear-solicitud`, solicitudCompleta)
      .then((response) => {
        console.log("Respuesta del servidor:", response.data);
      })
      .catch((error) => {
        console.error("Error al enviar la solicitud:", error);
      });
  };

  return (
    <Box>
      {/* HERO SECTION */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${mainBlue} 0%, #1e2a4a 100%)`,
          py: 6,
          px: 4,
          mb: 4,
          color: "white",
          textAlign: "center",
          boxShadow: "0px 4px 15px rgba(0,0,0,0.1)",
        }}
      >
        <Typography variant="h2" fontWeight="bold" sx={{ mb: 1 }}>
          Protección del Usuario
        </Typography>
        <Typography variant="h5" sx={{ opacity: 0.9 }}>
          Gestión paso a paso de tu solicitud de denuncia
        </Typography>
      </Box>

      {/* FORM CONTAINER */}
      <Box sx={{ maxWidth: "1000px", mx: "auto", px: 2, pb: 8 }}>
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: "16px",
            backgroundColor: theme.palette.mode === "dark" ? colors.primary[400] : "#ffffff",
          }}
        >
          {step === 1 && (
            <>
              <Box mb={4}>
                <Typography variant="h4" fontWeight="bold" color={mainBlue} mb={3} textAlign="center">
                  1. Datos del Denunciante
                </Typography>
                <Box display="flex" justifyContent="center">
                  <Box sx={{ width: "100%", maxWidth: "400px" }}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel id="representacion-label" sx={{ color: mainBlue }}>
                        Representación de la persona
                      </InputLabel>
                      <Select
                        labelId="representacion-label"
                        id="representacion-select"
                        value={representacion || "--Seleccione--"}
                        label="Representación de la persona"
                        onChange={handleSelectChange}
                        sx={{
                          borderRadius: "8px",
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "rgba(0,0,0,0.1)",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: mainBlue,
                          },
                        }}
                      >
                        <MenuItem value="--Seleccione--" disabled>
                          --Seleccione--
                        </MenuItem>
                        <MenuItem value="natural">Natural</MenuItem>
                        <MenuItem value="juridico">Jurídico</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              </Box>

              <Box>
                {representacion === "natural" && (
                  <RepresentacionNatural
                    data={formData.natural}
                    onNext={() => setStep(2)}
                    onDataChange={(data) => handleDataChange(data, "natural")}
                  />
                )}

                {representacion === "juridico" && (
                  <RepresentacionJuridica
                    data={formData.juridico}
                    onNext={() => setStep(2)}
                    onDataChange={(data) => handleDataChange(data, "juridico")}
                  />
                )}
              </Box>
            </>
          )}

          {step === 2 && (
            <Box>
              <Typography variant="h4" fontWeight="bold" color={mainBlue} mb={4} textAlign="center">
                2. Datos del Proveedor
              </Typography>
              <Proveedor
                data={formData.proveedor}
                onNext={() => setStep(3)}
                onBack={() => setStep(1)}
                onDataChange={(data) => handleDataChange(data, "proveedor")}
              />
            </Box>
          )}

          {step === 3 && (
            <Box>
              <Typography variant="h4" fontWeight="bold" color={mainBlue} mb={4} textAlign="center">
                3. Detalle de la Denuncia
              </Typography>
              <DenunciaForm
                data={formData.denuncia}
                onBack={() => setStep(2)}
                onSubmit={(data) => handleSubmit(data)}
              />
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default ProteccionUsuario;
